import { useMemo } from 'react';
import type React from 'react';
import { Button, Dropdown as AntDropdown } from 'antd';
import type { DropdownProps as AntDropdownProps, MenuProps } from 'antd';
import { MoreOutlined } from '@ant-design/icons';

import { withAuthGuard } from '../../auth';
import type { AuthProps } from '../../auth';
import { useUIText } from '../../i18n';

export interface DropdownMenuItem {
    key: string;
    label: React.ReactNode;
    onSelect?: () => void;
    icon?: React.ReactNode;
    danger?: boolean;
    disabled?: boolean;
}

export interface DropdownMenuProps extends Omit<AntDropdownProps, 'menu' | 'children'>, AuthProps {
    /** Declarative row-actions list, mapped onto antd `Dropdown`'s `menu.items`. */
    items: DropdownMenuItem[];
    /** Custom trigger element. Defaults to an ellipsis icon button with an accessible name. */
    children?: React.ReactNode;
    /**
     * Accessible name for the default ellipsis trigger button. Defaults to
     * the shipped `common.actions` catalog string ("Actions"/"Thao tác") —
     * pass a more specific label (e.g. including the row's title) where that
     * context is available at the call site.
     */
    triggerAriaLabel?: string;
}

function DropdownMenuBase({
    items,
    children,
    trigger = ['click'],
    triggerAriaLabel,
    ...rest
}: Omit<DropdownMenuProps, 'auth' | 'authFallback'>): React.ReactElement {
    const { common } = useUIText();

    const menu: MenuProps = useMemo(
        () => ({
            items: items.map((item) => ({
                key: item.key,
                label: item.label,
                icon: item.icon,
                danger: item.danger,
                disabled: item.disabled,
                onClick: item.onSelect,
            })),
        }),
        [items]
    );

    return (
        <AntDropdown menu={menu} trigger={trigger} {...rest}>
            {children ?? (
                <Button
                    type="text"
                    icon={<MoreOutlined />}
                    aria-label={triggerAriaLabel ?? common.actions}
                />
            )}
        </AntDropdown>
    );
}

const GuardedDropdownMenu = withAuthGuard(DropdownMenuBase, {
    defaultFallback: 'disable',
}) as unknown as React.ForwardRefExoticComponent<
    DropdownMenuProps & React.RefAttributes<HTMLElement>
>;

/**
 * Tier-1 presentational primitive: guarded wrapper over antd `Dropdown` that
 * takes a declarative `items` list (`DropdownMenuItem[]`) instead of raw
 * `menu` config, so every row-actions menu across the apps looks and behaves
 * the same. `danger` maps onto antd's own per-menu-item `danger` flag (a red
 * label, not a destructive-action confirmation — pair a `danger` item's
 * `onSelect` with `ConfirmButton`/`Popconfirm` for that). `authFallback`
 * defaults to `"disable"`, matching `Button`: an ungranted menu still shows
 * its trigger, disabled, rather than disappearing.
 */
export const DropdownMenu = GuardedDropdownMenu;

export default DropdownMenu;
